import { useEffect, useState, type FormEvent } from 'react';
import { useLocation } from 'wouter';
import { AuthService } from '../lib/auth';
import {
  startExperimentDirectInterview,
  startFreshExperimentDirectInterview,
} from '../lib/experiment-direct-entry';
import { queryClient } from '../lib/queryClient';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Shield, AlertCircle, CheckCircle, Mail, Globe, UserRound } from 'lucide-react';
import type { AuthStatus, StaffRole } from '../types';
import { toUserFacingError } from '../lib/user-facing-error';

function staffLandingPath(role: string | null | undefined): string | null {
  if (role === 'admin') return '/admin';
  if (role === 'educator') return '/teacher';
  return null;
}

const STAFF_VIEW_OPTIONS: Array<{ role: StaffRole; label: string }> = [
  { role: 'student', label: '学生' },
  { role: 'educator', label: '教員' },
  { role: 'admin', label: '管理者' },
];

export default function Login() {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [authStatus, setAuthStatus] = useState<AuthStatus | null>(null);
  const [error, setError] = useState<string>('');
  const [participantCode, setParticipantCode] = useState('');
  const [guestPassword, setGuestPassword] = useState('');
  const initialParams = new URLSearchParams(window.location.search);
  const staffMode = initialParams.get('staff') === '1';
  const requestedRole = initialParams.get('as');
  const initialStaffViewRole = STAFF_VIEW_OPTIONS.some((option) => option.role === requestedRole)
    ? requestedRole as StaffRole
    : 'student';
  const [staffViewRole, setStaffViewRole] = useState<StaffRole>(initialStaffViewRole);
  const [, setLocation] = useLocation();
  const authService = AuthService.getInstance();

  useEffect(() => {
    const checkAuthAndRedirect = async () => {
      try {
        const status = await authService.getAuthStatus({ forceRefresh: true });
        setAuthStatus(status);
        const roleSelectionEnabled = staffMode || status.authMode === 'google';

        if (status.isAuthenticated) {
          if (roleSelectionEnabled) {
            const switched = await authService.setStaffViewRole(staffViewRole);
            queryClient.invalidateQueries({ queryKey: ['authStatus'] });
            queryClient.invalidateQueries({ queryKey: ['currentUser'] });
            queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
            setLocation(switched.landingPath);
            return;
          }
          const currentUser = await authService.getCurrentUser();
          const staffPath = staffLandingPath(currentUser?.role ?? status.user?.role);
          if (staffPath) {
            setLocation(staffPath);
            return;
          }
        }

        // 認証不要または既に認証済みの場合は適切な開始画面へ
        if (status.authMode === 'experiment' && status.experimentEntryFlow === 'ab_direct_interview') {
          if (status.isAuthenticated) {
            const currentUser = await authService.getCurrentUser();
            const directEntryUser = currentUser ?? status.user;
            if (directEntryUser?.experimentEntryFlow === 'ab_direct_interview' && directEntryUser.experimentEntryTarget) {
              await startExperimentDirectInterview(directEntryUser.experimentEntryTarget);
              setLocation('/interview');
              return;
            }
            setError('実証実験の面接開始情報が見つかりません。もう一度ログインしてください。');
          }
          return;
        }

        if (!status.authRequired || status.isAuthenticated) {
          if (status.authMode === 'experiment' && status.isAuthenticated) {
            const currentUser = await authService.getCurrentUser();
            if (currentUser?.experimentEntryFlow === 'ab_direct_interview' && currentUser.experimentEntryTarget) {
              await startExperimentDirectInterview(currentUser.experimentEntryTarget);
              setLocation('/interview');
              return;
            }
          }
          setLocation('/select-filter');
          return;
        }
      } catch (error) {
        console.error('認証状態確認エラー:', error);
        setError('認証状態の確認に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    const handleAuthError = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const errorType = urlParams.get('error');
      const message = urlParams.get('message');

      if (errorType === 'auth_failed') {
        setError(message || '認証に失敗しました。許可されていないアカウントの可能性があります。');
      } else if (errorType === 'token_failed') {
        setError('認証トークンの生成に失敗しました。再度お試しください。');
      } else if (errorType === 'google_unavailable') {
        setError('Googleログイン設定が有効ではありません。管理者に確認してください。');
      } else if (errorType === 'account_disabled') {
        setError('このアカウントは無効化されています。管理者にお問い合わせください。');
      }
    };

    checkAuthAndRedirect();
    handleAuthError();
  }, [authService, setLocation, staffMode, staffViewRole]);

  const handleGoogleLogin = () => {
    const roleSelectionEnabled = staffMode || authStatus?.authMode === 'google';
    authService.startGoogleAuth(roleSelectionEnabled ? staffViewRole : undefined);
  };

  const handleParticipantCodeChange = (value: string) => {
    if (/^[A-Za-z0-9]*$/.test(value)) {
      setParticipantCode(value);
      if (error === '学籍番号は半角英数字のみで入力してください。') {
        setError('');
      }
      return;
    }

    setError('学籍番号は半角英数字のみで入力してください。');
  };

  const handleExperimentEntry = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');

    const trimmedCode = participantCode.trim();
    if (!trimmedCode) {
      setError('学籍番号を入力してください。');
      return;
    }
    if (!/^[A-Za-z0-9]+$/.test(trimmedCode)) {
      setError('学籍番号は半角英数字のみで入力してください。');
      return;
    }

    try {
      setSubmitting(true);
      const entry = await authService.startExperimentEntryDetailed({
        participantCode: trimmedCode,
        password: guestPassword.trim() || undefined,
      });
      const user = entry.user;
      queryClient.setQueryData(['authStatus'], {
        authRequired: true,
        authMode: 'experiment',
        experimentEntryFlow: authStatus?.experimentEntryFlow,
        experimentGroups: authStatus?.experimentGroups ?? [],
        isAuthenticated: true,
        user,
        allowedDomains: [],
        hasEmailRestrictions: false,
      });
      queryClient.setQueryData(['currentUser'], user);
      if (entry.entryTarget?.flow === 'ab_direct_interview') {
        await startFreshExperimentDirectInterview(entry.entryTarget);
        queryClient.invalidateQueries({ queryKey: ['/api/avatar/current'] });
        setLocation('/interview');
        return;
      }
      if (isDirectEntryFlow) {
        setError('A/B組の面接開始情報が見つかりません。管理者に設定を確認してください。');
        return;
      }
      setLocation('/select-filter');
    } catch (error) {
      setError(toUserFacingError(error, 'login').description);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-medical-light">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-medical-blue mb-4"></div>
            <p className="text-gray-600">読み込み中...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isExperimentMode = authStatus?.authMode === 'experiment';
  const showExperimentEntry = isExperimentMode && !staffMode;
  const isDirectEntryFlow = authStatus?.experimentEntryFlow === 'ab_direct_interview';
  const showStaffViewSelector = staffMode || authStatus?.authMode === 'google';

  return (
    <div className="min-h-screen flex items-center justify-center bg-medical-light px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-medical-blue rounded-full flex items-center justify-center">
            {showExperimentEntry ? (
              <UserRound className="h-8 w-8 text-white" />
            ) : (
              <Shield className="h-8 w-8 text-white" />
            )}
          </div>
          <CardTitle className="text-2xl font-bold text-gray-900">
            {showExperimentEntry ? 'ログイン' : 'デンタルアバター'}
          </CardTitle>
          <p className="text-gray-600 mt-2">
            {showExperimentEntry
              ? '学籍番号とパスワードを入力してください'
              : '歯科衛生士学生のコミュニケーション訓練システム'}
          </p>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {error && (
            <Alert className="border-red-200 bg-red-50">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-700">
                {error}
              </AlertDescription>
            </Alert>
          )}

          {showExperimentEntry ? (
            <form className="space-y-4" onSubmit={handleExperimentEntry}>
              <label className="block">
                <span className="mb-1 block text-sm font-bold text-gray-700">学籍番号</span>
                <input
                  value={participantCode}
                  onChange={(event) => handleParticipantCodeChange(event.target.value)}
                  className="h-12 w-full rounded-lg border border-gray-300 bg-white px-4 text-base text-gray-900 outline-none focus:border-medical-blue focus:ring-2 focus:ring-blue-100"
                  placeholder="学籍番号を入力"
                  maxLength={32}
                  autoComplete="username"
                  inputMode="text"
                  pattern="[A-Za-z0-9]*"
                  required
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-sm font-bold text-gray-700">パスワード</span>
                <input
                  type="password"
                  value={guestPassword}
                  onChange={(event) => setGuestPassword(event.target.value)}
                  className="h-12 w-full rounded-lg border border-gray-300 bg-white px-4 text-base text-gray-900 outline-none focus:border-medical-blue focus:ring-2 focus:ring-blue-100"
                  placeholder="配布されたパスワードを入力"
                  maxLength={64}
                  autoComplete="current-password"
                  inputMode="text"
                  required
                />
              </label>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-medical-blue hover:bg-blue-600 py-3 text-base font-bold"
                size="lg"
              >
                {submitting ? 'ログイン中...' : 'ログイン'}
              </Button>
              <div className="pt-2 text-center">
                <a href="/login?staff=1" className="text-sm font-medium text-medical-blue hover:underline">
                  管理者・教員はこちら
                </a>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              {showStaffViewSelector && (
                <div>
                  <div className="mb-2 text-sm font-bold text-gray-700">ログイン後の表示</div>
                  <div className="grid grid-cols-3 rounded-lg border border-gray-300 bg-gray-50 p-1" aria-label="表示ロール">
                    {STAFF_VIEW_OPTIONS.map((option) => (
                      <button
                        key={option.role}
                        type="button"
                        onClick={() => setStaffViewRole(option.role)}
                        className={`rounded-md px-3 py-2 text-sm font-bold transition-colors ${
                          staffViewRole === option.role
                            ? 'bg-medical-blue text-white shadow-sm'
                            : 'text-gray-600 hover:bg-white'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-gray-500">DBのロールは変更せず、今回使う画面だけ切り替えます。</p>
                </div>
              )}
              <Button
                onClick={handleGoogleLogin}
                className="w-full bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-3 py-3"
                size="lg"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Googleでログイン
              </Button>
              {isExperimentMode && staffMode && (
                <div className="text-center">
                  <a href="/login" className="text-sm font-medium text-medical-blue hover:underline">
                    参加者ログインに戻る
                  </a>
                </div>
              )}
            </div>
          )}

          {authStatus && !showExperimentEntry && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                アクセス制限情報
              </h3>
              
              {authStatus.hasEmailRestrictions && (
                <div className="mb-2">
                  <Badge variant="outline" className="text-xs">
                    <Mail className="h-3 w-3 mr-1" />
                    特定のメールアドレスのみ許可
                  </Badge>
                </div>
              )}
              
              {authStatus.allowedDomains.length > 0 && (
                <div className="mb-2">
                  <Badge variant="outline" className="text-xs">
                    <Globe className="h-3 w-3 mr-1" />
                    許可ドメイン
                  </Badge>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {authStatus.allowedDomains.map((domain: string) => (
                      <Badge key={domain} variant="secondary" className="text-xs">
                        @{domain}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              <p className="text-xs text-gray-600 mt-2">
                アクセスが許可されていないアカウントではログインできません。
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
